import { CHANNEL_LABELS, CHANNELS, fmtDate, httpUrl, isClosed, whenText, type Channel } from '@jt/core';
import type { ApplicationFull } from '@jt/shared';
import { useRef, useState, type FormEvent } from 'react';
import { useAppActions, useTodayDate } from '../api/hooks';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon, type IconName } from '../components/ui';
import { openPaste } from '../drafts/openPaste';
import { useAi } from '../drafts/useDraft';

export const CHANNEL_ICON: Record<Channel, IconName> = {
  LinkedIn: 'linkedin',
  Email: 'mail',
  Telegram: 'send',
  Call: 'phone',
  Meeting: 'calendar',
  System: 'info',
  Other: 'msg',
};

export function HistoryTab({ a }: { a: ApplicationFull }) {
  const act = useAppActions();
  const ui = useUI();
  const ai = useAi();
  const today = useTodayDate();
  const formRef = useRef<HTMLDetailsElement>(null);
  const [err, setErr] = useState('');
  const hasEmail = a.contacts.some((c) => c.email);

  const add = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const text = String(f.get('text') ?? '').trim();
    if (!text) {
      setErr('Опишите, что было.');
      return;
    }
    try {
      await act.addActivity(a.id, {
        occurredOn: String(f.get('date') || today),
        channel: String(f.get('channel')),
        direction: String(f.get('dir')),
        text,
      });
      form.reset();
      setErr('');
      if (formRef.current) formRef.current.open = false;
      ui.toast('Запись добавлена');
    } catch {
      setErr('Не сохранилось. Повторите.');
    }
  };

  const items = a.activities;
  const plan = a.nextDate && !isClosed(a.stage);
  return (
    <>
      {items.length || plan ? (
        <ul className="tl">
          {items.map((l) => (
            <li key={l.id} className={l.direction}>
              <span className="evic">
                <Icon name={CHANNEL_ICON[l.channel]} />
              </span>
              <div className="evb">
                <div className="evh">
                  <span>
                    {l.channel === 'System' ? 'Событие' : CHANNEL_LABELS[l.channel]}
                    {l.direction === 'in' ? ' · от них' : l.direction === 'out' ? ' · от вас' : ''} ·{' '}
                    {fmtDate(l.occurredOn)}
                  </span>
                  <ConfirmButton
                    className="x"
                    confirmText="Удалить?"
                    onConfirm={() => act.deleteActivity(a.id, l.id).catch(() => ui.toast('Не удалилось'))}
                  >
                    <span aria-label="Удалить запись">✕</span>
                  </ConfirmButton>
                </div>
                <div className="evt">{l.text}</div>
                {l.externalUrl && (
                  <a
                    className="sm"
                    href={httpUrl(l.externalUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--accent)' }}
                  >
                    Открыть письмо
                  </a>
                )}
                {l.raw && (
                  <details className="raw">
                    <summary>Исходный текст</summary>
                    <pre>{l.raw}</pre>
                  </details>
                )}
              </div>
            </li>
          ))}
          {plan && (
            <li className="plan">
              <span className="evic">
                <Icon name="calendar" />
              </span>
              <div className="evb">
                <div className="evh">
                  <span>{whenText(a.nextDate, a.nextTime, today)}</span>
                </div>
                <div className="evt">{a.nextStep || 'Следующий шаг'}</div>
              </div>
            </li>
          )}
        </ul>
      ) : (
        <p className="muted">Записей пока нет.</p>
      )}
      <details className="pform" ref={formRef}>
        <summary className="sm" style={{ cursor: 'pointer' }}>
          <b>+ Добавить запись</b>
        </summary>
        <form onSubmit={add}>
          <div className="grid" style={{ marginTop: 10 }}>
            <label className="f">
              Канал
              <select name="channel" defaultValue={hasEmail ? 'Email' : 'LinkedIn'}>
                {CHANNELS.filter((c) => c !== 'System').map((c) => (
                  <option key={c} value={c}>
                    {CHANNEL_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="f">
              Кто написал
              <select name="dir" defaultValue="in">
                <option value="in">Мне написали</option>
                <option value="out">Я написал</option>
              </select>
            </label>
            <label className="f">
              Дата
              <input type="date" name="date" defaultValue={today} />
            </label>
            <label className="f full">
              Что было
              <textarea name="text" rows={3} placeholder="Кратко: что сказали, о чём договорились" />
            </label>
          </div>
          {err && <p className="fnote err">{err}</p>}
          <div className="btnrow" style={{ marginTop: 10 }}>
            <button className="btn sm primary" type="submit">
              Добавить запись
            </button>
          </div>
        </form>
      </details>
      {ai && (
        <button className="btn sm ghost" onClick={() => openPaste(ui, a.id, a.company)}>
          <Icon name="clip" size={16} /> Разобрать сообщение
        </button>
      )}
    </>
  );
}
